/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React, { useState } from 'react';
import { Collapse, Divider, makeStyles, Typography, useMediaQuery } from '@material-ui/core';
import { ExpandLess, ExpandMore } from '@material-ui/icons';
import {
  ConfirmationPackageDownload,
  ConfirmationPreviewFixedCards,
  PreviewFilesSelect,
  PreviewHeader,
  SidebarAttachmentCards,
  SubmissionPreviewActions,
} from '../../components/preview';
import useSendConfirmation from './useSendConfirmation';
import ConfirmationPreviewCanvas from '../../components/preview/ConfirmationPreviewCanvas';
import { getProcedureName } from '../../procedure/SubmissionProcedure';
import Button from '../../components/common/Button';
import useSubmissionPreview from '../submissionPreview/useSubmissionPreview';
import Breadcrumb from '../../components/common/Breadcrumb';
import { getSubmissionTitle } from '../../shared/submission/submissionPropertyAccessor';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr 1fr',
    gridTemplateRows: 'max-content max-content max-content max-content max-content min-content',
    gridTemplateAreas:
      '' +
      '"breadcrumbs breadcrumbs breadcrumbs breadcrumbs"' +
      '"header header header header"' +
      '"staticText staticText staticText staticText"' +
      '"actions actions actions actions"' +
      '"preview preview preview preview"' +
      '"canvas canvas canvas canvas"',
    margin: '1.5rem 0',
    padding: '0 1rem',
    minHeight: 'calc(100vh - 10.1875rem)', // 100% of viewport - (header + footer)
    [theme.breakpoints.up('md')]: {
      gridTemplateAreas:
        '' +
        '"breadcrumbs breadcrumbs breadcrumbs breadcrumbs"' +
        '"header header header header"' +
        '"staticText staticText staticText staticText"' +
        '". . . ."' +
        '"select select actions actions"' +
        '"preview preview preview preview"' +
        '"canvas canvas canvas canvas"',
      padding: '0 1.5rem',
    },
    [theme.breakpoints.up('lg')]: {
      minHeight: 'calc(100vh - 11.1875rem)',
      margin: '2rem 0',
      marginLeft: '8.75rem',
      paddingRight: '1.5rem',
    },
    [theme.breakpoints.up('xl2')]: {
      gridTemplateAreas:
        '' +
        '"breadcrumbs breadcrumbs breadcrumbs breadcrumbs"' +
        '"header header header header"' +
        '"staticText staticText actions actions"' +
        '". . . ."' +
        '"sidebar canvas canvas canvas"',
      padding: '0',
      paddingRight: '2rem',
      marginLeft: '8.5rem',
    },
  },
  breadcrumbs: {
    gridArea: 'breadcrumbs',
  },

  headerContainer: {
    gridArea: 'header',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
  },
  headerContent: {
    margin: '1rem 0',
    [theme.breakpoints.up('xl2')]: {
      margin: '2rem 0',
    },
  },
  staticText: {
    gridArea: 'staticText',
    marginTop: '2.5rem',
    marginBottom: '1rem',
    [theme.breakpoints.up('md')]: {
      marginBottom: '2.5rem',
    },
  },
  download: {
    [theme.breakpoints.between('sm', 'md')]: {
      marginTop: '1.5rem',
      marginBottom: '2.25rem',
    },
  },
  sidebar: {
    gridArea: 'sidebar',
  },
  select: {
    gridArea: 'select',
    marginBottom: '1.5rem',
  },
  canvas: {
    gridArea: 'canvas',
  },
  divider: {
    maxWidth: 345,
  },
  expandButton: {
    background: 'none',
    minWidth: 150,
    fontWeight: 400,
    marginTop: '1rem',
    marginBottom: '1rem',
    padding: 0,
    '&:hover': {
      backgroundColor: 'transparent',
    },
    '&:focus': {
      outline: 'none',
    },
  },
  preview: {
    gridArea: 'preview',
    display: 'flex',
    marginBottom: '1.5rem',
    flexDirection: 'column-reverse',
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
  },
  actions: {
    gridArea: 'actions',

    [theme.breakpoints.up('md')]: {
      justifySelf: 'flex-end',
    },

    marginBottom: '1.5rem',
    alignSelf: 'flex-end',
    [theme.breakpoints.up('xl2')]: {
      marginBottom: '2.5rem',
      alignSelf: 'flex-start',
    },
  },
}));

export default function SendConfirmation() {
  const classes = useStyles();

  const {
    submission,
    uiSchema,
    topSelectEntries,
    topSidebarEntries,
    attachments,
    previewObject,
    t,
    loadAttachmentFile,
    handleReceiptClick,
    handleSignaturesClick,
    handleSummaryClick,
  } = useSendConfirmation();

  const { bodyButtonsConfig } = useSubmissionPreview();

  const isExtraLargeOrBigger = useMediaQuery(theme => theme.breakpoints.up('xl2'));

  const [expandedAttachments, setExpandedAttachments] = useState(false);
  const procedureName = getProcedureName(submission);

  const handleExpandClick = () => {
    setExpandedAttachments(!expandedAttachments);
  };

  return uiSchema ? (
    <div className={classes.container}>
      <Breadcrumb
        className={classes.breadcrumbs}
        currentBreadcrumb={t('Preview.title', { title: getSubmissionTitle(submission, uiSchema) })}
      />
      <div className={classes.headerContainer}>
        <PreviewHeader
          className={classes.headerContent}
          submission={submission}
          headerTitle={t('messages.submission.send.confirm', {
            procedure: procedureName,
          })}
        />
        <div>
          <SubmissionPreviewActions className={classes.actions} buttonsConfig={bodyButtonsConfig} />
        </div>
      </div>

      <Typography className={classes.staticText}>
        {t('confirmation.staticText', { procedure: procedureName })}
      </Typography>

      {isExtraLargeOrBigger ? (
        <div style={{ gridArea: 'sidebar' }}>
          <ConfirmationPreviewFixedCards
            className={classes.sidebar}
            previewObject={previewObject}
            handleReceiptClick={handleReceiptClick}
            handleSignaturesClick={handleSignaturesClick}
            handleSummaryClick={handleSummaryClick}
          />
          <Divider className={classes.divider} />
          <Button
            className={classes.expandButton}
            color="secondary"
            onClick={() => handleExpandClick()}
            endIcon={expandedAttachments ? <ExpandLess /> : <ExpandMore />}
            disableRipple={true}
            focusVisibleClassName=""
          >
            {t('Submission')}
          </Button>

          <Collapse in={expandedAttachments} timeout="auto">
            <SidebarAttachmentCards
              submission={submission}
              topEntries={topSidebarEntries}
              attachments={attachments}
              previewObject={previewObject}
              loadAttachmentFile={loadAttachmentFile}
            />
            {(submission?.securityVetting !== 'DENIED' || submission?.packageData?.packageFile) && (
              <ConfirmationPackageDownload className={classes.download} submission={submission} />
            )}
          </Collapse>
        </div>
      ) : (
        <div className={classes.preview}>
          <PreviewFilesSelect
            submission={submission}
            topEntries={topSelectEntries}
            attachments={attachments}
            previewObject={previewObject}
            handleReceiptClick={handleReceiptClick}
            handleSummaryClick={handleSummaryClick}
            loadAttachmentFile={loadAttachmentFile}
          />
          {(submission?.securityVetting !== 'DENIED' || submission?.packageData?.packageFile) && (
            <ConfirmationPackageDownload className={classes.download} submission={submission} />
          )}
        </div>
      )}

      <ConfirmationPreviewCanvas
        className={classes.canvas}
        submission={submission}
        previewObject={previewObject}
        selectedCard={previewObject?.id}
        uiSchema={uiSchema}
      />
    </div>
  ) : (
    <></>
  );
}
